import Query from "./query";
import credentials from "./credentials";

export interface ApiResponse<T> {
  items?: T[];
  data?: T;
  total?: number;
}

export default class Client {
  private basePath: string;
  private baseUrl: string;

  constructor(name: string, version: string, domain?: string) {
    const apiUrl = import.meta.env.VITE_APP_API_URL || 'https://api.dev.edreports.org';
    this.basePath = `_ah/api/${name}/${version}/`;
    this.baseUrl = apiUrl.endsWith('/') ? apiUrl : apiUrl + '/';
  }

  /**
   * Executes an endpoint and returns an object containing the resulting promise along with the XMLHttpRequest object.
   * @param resource the endpoint to call
   * @param data the data to send with the query
   * @param method the HTTP method for the query, defaults to GET.
   * @param options additional fetch options like signal for AbortController
   * @return an object containing the Promise for the request along with the XMLHttpRequest object.
   */
  request(resource: string, data?: any, method?: string, options?: RequestInit) {
    const uri = this.baseUrl + resource;
    return new Query(uri, data, method, options);
  }

  /**
   * Executes an endpoint.
   * @param resource the endpoint to call
   * @param data the data to send with the query
   * @param method the HTTP method for the query, defaults to GET.
   * @param options additional fetch options like signal for AbortController
   * @return a Promise for the request.
   */
  execute(resource: string, data?: any, method?: string, options?: RequestInit) {
    const requestOptions = { ...options };
    
    return this.request(resource, data, method, requestOptions).response
      .catch(error => {
        if (error && error.name === 'AbortError') {
          throw error;
        }
        
        if (error && (
          error.message.includes('network') || 
          error.message.includes('timeout') ||
          error.message.includes('connection')
        )) {
          return new Promise((resolve, reject) => {
            setTimeout(() => {
              const retryOptions = {...options};
              if (retryOptions && retryOptions.signal) {
                delete retryOptions.signal;
              }
              
              this.request(resource, data, method, retryOptions).response
                .then(resolve)
                .catch(reject);
            }, 1000);
          });
        }
        
        throw error;
      });
  }

  /**
   * Synchronously loads a complete list of items from an endpoint using a `cursor`.
   * Fails if endpoint does not support `cursor` requests.
   * @param resource the endpoint to access.
   * @param data object to pass to the request.
   * @param limit the default number of items per "chunk" request, will be overwritten if set in data.
   * @param cursor a starting cursor.
   * @returns {Promise} a promise resolving as an array of the requested items.
   */
  loadChunks(resource: string, data: any = {}, limit: number = 100, cursor: string | null = null): Promise<any[]> {
    return new Promise((resolve, reject) => {
      let items: any[] = [];

      if (data.cursor) {
        cursor = data.cursor;
        data.cursor = undefined;
      }

      const request = Object.assign({limit: limit}, data);
      if (cursor) {
        request.cursor = cursor;
      }

      this.execute(resource, request).then(response => {
        const chunk = response.items;

        if (Array.isArray(chunk) && 0 < chunk.length) {
          items = items.concat(chunk);
          if (chunk.length < limit) {
            resolve(items);
          } else {
            this.loadChunks(resource, data, limit, response.nextPageToken)
              .then(chunk => {
                items = items.concat(chunk);
                resolve(items);
              });
          }
        } else {
          resolve(items);
        }
      }).catch((reason) => {
        reject(reason);
      });
    });
  }
} 